"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  differenceInDays,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parse,
  startOfWeek,
} from "date-fns";
import { AnimatePresence } from "motion/react";
import { toZonedTime } from "date-fns-tz";

import { type CalendarConfig, defaultConfig } from "../types/calendar-config";
import { StudentSlotComponent } from "../components/student/StudentSlotComponent.tsx";

import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { CalendarGridBase } from "@/features/calendar/components/shared/CalendarGridBase";
import { CalendarHourCell } from "@/features/calendar/components/shared/CalendarHourCell";
import { CalendarCurrentTimeIndicator } from "@/features/calendar/components/shared/CalendarCurrentTimeIndicator";
import GenericLoader from "@/components/GenericLoader.tsx";

interface StudentCalendarViewProps {
  config?: Partial<CalendarConfig>;
  canMakeReservations?: boolean;
}

export default function StudentCalendarView({
  config = {},
  canMakeReservations,
}: StudentCalendarViewProps) {
  const { shownDays, calendarData, setDateRange, dateRange, isLoading } = useCalendarStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const timeIndicatorRef = useRef<HTMLDivElement>(null);
  const finalConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);

  const hours = useMemo(
    () =>
      Array.from(
        { length: finalConfig.maxHour - finalConfig.minHour },
        (_, i) => i + finalConfig.minHour,
      ),
    [finalConfig.maxHour, finalConfig.minHour],
  );

  const getWeekDays = () => {
    const days: Date[] = [];

    for (let i = 0; i < shownDays; i++) {
      const day = parse(dateRange.from, "yyyy-MM-dd", new Date());

      day.setDate(day.getDate() + i);
      days.push(day);
    }

    return days;
  };
  const weekDays = getWeekDays();

  useEffect(() => {
    setCurrentTime(toZonedTime(new Date(), calendarData?.displayTimezone ?? "UTC"));
    const timer = setInterval(
      () => setCurrentTime(toZonedTime(new Date(), calendarData?.displayTimezone ?? "UTC")),
      60000,
    );

    return () => clearInterval(timer);
  }, [calendarData]);

  // Auto-scroll to time indicator on initial load
  useEffect(() => {
    timeIndicatorRef.current?.scrollIntoView({
      behavior: "instant",
      block: "center",
      inline: "center",
    });
  }, [calendarData]);
  useEffect(() => {
    let newStartDate, newEndDate;

    const diff = differenceInDays(
      parse(dateRange.from, "yyyy-MM-dd", new Date()),
      parse(dateRange.to, "yyyy-MM-dd", new Date()),
    );

    if (diff < shownDays) {
      switch (shownDays) {
        case 7:
          newStartDate = format(
            startOfWeek(parse(dateRange.from, "yyyy-MM-dd", new Date()), { weekStartsOn: 1 }),
            "yyyy-MM-dd",
          );
          newEndDate = format(
            endOfWeek(parse(dateRange.from, "yyyy-MM-dd", new Date()), { weekStartsOn: 1 }),
            "yyyy-MM-dd",
          );
          break;
        case 3:
        case 1:
          newStartDate = format(parse(dateRange.from, "yyyy-MM-dd", new Date()), "yyyy-MM-dd");
          newEndDate = format(
            addDays(parse(dateRange.from, "yyyy-MM-dd", new Date()), shownDays - 1),
            "yyyy-MM-dd",
          );
          break;
      }
      if (
        calendarData &&
        isAfter(
          parse(newEndDate, "yyyy-MM-dd", new Date()),
          parse(calendarData.calendarEndsAt, "yyyy-MM-dd", new Date()),
        )
      ) {
        newEndDate = calendarData?.calendarEndsAt;
      }
      if (
        calendarData &&
        isBefore(
          parse(newStartDate, "yyyy-MM-dd", new Date()),
          parse(calendarData.calendarStartsAt, "yyyy-MM-dd", new Date()),
        )
      ) {
        newStartDate = calendarData?.calendarStartsAt;
      }
      if (
        newStartDate &&
        newEndDate &&
        (newStartDate !== dateRange.from || newEndDate !== dateRange.to)
      ) {
        setDateRange({
          from: newStartDate,
          to: newEndDate,
        });
      }
    }
  }, [shownDays]);

  const getEventsForDate = useCallback(
    (date: Date) => {
      let eventsForDate;

      if (calendarData && calendarData.data) {
        eventsForDate = calendarData.data.filter((item) => {
          return format(item.startTimeDateTimeString, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
        });

        return eventsForDate;
      }

      return [];
    },
    [calendarData],
  );

  return (
    <CalendarGridBase
      canMakeReservations={canMakeReservations}
      headerHeight={finalConfig.headerHeight}
      hourHeight={finalConfig.hourHeight}
      hours={hours}
      isLoading={isLoading}
      minHour={finalConfig.minHour}
      shownDays={shownDays}
      timeColumnWidth={finalConfig.timeColumnWidth}
      weekDays={weekDays}
    >
      {isLoading && <GenericLoader className={"fixed top-0 left-0 z-30"} />}
      {weekDays.slice(0, shownDays).map((day, dayIndex) => (
        <div key={`${day.toISOString()}`} className="relative">
          {hours.map((hour) => (
            <CalendarHourCell key={hour} hour={hour} isLoading={isLoading}>
              {isSameDay(day, currentTime) && currentTime.getHours() === hour && (
                <CalendarCurrentTimeIndicator
                  ref={timeIndicatorRef}
                  currentTime={currentTime}
                  hourHeight={finalConfig.hourHeight}
                />
              )}
            </CalendarHourCell>
          ))}
          <AnimatePresence initial={true} mode={"popLayout"}>
            {!isLoading &&
              getEventsForDate(day).map((event) => (
                <StudentSlotComponent
                  key={event.id}
                  canMakeReservations={canMakeReservations}
                  dayIndex={dayIndex}
                  event={event}
                  finalConfig={finalConfig}
                  weekDaysLength={weekDays.length}
                />
              ))}
          </AnimatePresence>
        </div>
      ))}
    </CalendarGridBase>
  );
}
